import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import {
  COBRA, COLORS, ROW_DEPTH, FIELD_HALF_TILES, TILE_X,
} from '../core/Constants.js';

const X_LIMIT = (FIELD_HALF_TILES - 1) * TILE_X;

export const CobraState = {
  HIDDEN: 'hidden',
  EMERGE: 'emerge',
  ACTIVE: 'active',
  RETREAT: 'retreat',
};

function buildCobraHeadGeometry() {
  const G = COLORS.COBRA_BODY || 0x265c1b;
  const Y = COLORS.COBRA_BELLY || 0xdfb435;
  const D = COLORS.COBRA_MARKING || 0x10200a;
  const R = COLORS.COBRA_EYE || 0xe32219;

  const parts = [
    // ── 1. Upright Neck / Body Trunk ──────────────────
    box(0.46, 1.1, 0.36, G, 0, 0.65, 0),
    box(0.34, 1.0, 0.06, Y, 0, 0.65, 0.16),
    box(0.36, 0.05, 0.07, D, 0, 0.35, 0.16),
    box(0.36, 0.05, 0.07, D, 0, 0.55, 0.16),
    box(0.36, 0.05, 0.07, D, 0, 0.75, 0.16),
    box(0.36, 0.05, 0.07, D, 0, 0.95, 0.16),

    // ── 2. Flared Hood ────────────────────────────────
    box(0.38, 0.85, 0.1, G, -0.38, 0.85, 0.02),
    box(0.38, 0.85, 0.1, G, 0.38, 0.85, 0.02),
    box(0.3, 0.75, 0.04, Y, -0.34, 0.85, 0.08),
    box(0.3, 0.75, 0.04, Y, 0.34, 0.85, 0.08),
    // Back of hood spectacle marking
    box(0.76, 0.5, 0.04, D, 0, 0.88, -0.05),
    box(0.2, 0.28, 0.04, Y, -0.2, 0.88, -0.06),
    box(0.2, 0.28, 0.04, Y, 0.2, 0.88, -0.06),
    box(0.24, 0.1, 0.04, Y, 0, 0.96, -0.06),

    // ── 3. Head & Snout ───────────────────────────────
    box(0.52, 0.32, 0.58, G, 0, 1.32, 0.12),
    box(0.42, 0.22, 0.32, G, 0, 1.34, 0.38),
    box(0.38, 0.12, 0.45, Y, 0, 1.18, 0.22),
    // Menacing Red Eyes
    box(0.12, 0.14, 0.18, R, -0.24, 1.34, 0.22),
    box(0.12, 0.14, 0.18, R, 0.24, 1.34, 0.22),
    box(0.04, 0.1, 0.1, D, -0.27, 1.34, 0.24),
    box(0.04, 0.1, 0.1, D, 0.27, 1.34, 0.24),
  ];
  return weld(parts);
}

function buildCobraSegmentGeometry(i, numSegments) {
  const G = COLORS.COBRA_BODY || 0x265c1b;
  const Y = COLORS.COBRA_BELLY || 0xdfb435;
  const D = COLORS.COBRA_MARKING || 0x10200a;

  const t = i / Math.max(1, numSegments - 1);
  const w = Math.max(0.14, 0.48 * (1.0 - t * 0.65));
  const h = Math.max(0.12, 0.36 * (1.0 - t * 0.60));
  const len = 0.46;

  const parts = [
    // Main body back (green)
    box(w, h, len, G, 0, h / 2, 0),
    // Yellow belly underside
    box(w * 0.72, Math.max(0.04, h * 0.3), len * 0.95, Y, 0, h * 0.15, 0),
    // Dorsal scale stripe/marking along back
    box(w * 0.35, 0.04, len * 0.6, D, 0, h + 0.01, 0),
  ];
  return weld(parts);
}

function buildTongueGeometry() {
  const T = COLORS.COBRA_TONGUE || 0xdd1e1e;
  const parts = [
    box(0.06, 0.04, 0.32, T, 0, 1.21, 0.48),
    box(0.04, 0.04, 0.14, T, -0.05, 1.21, 0.66),
    box(0.04, 0.04, 0.14, T, 0.05, 1.21, 0.66),
  ];
  return weld(parts);
}

export class Cobra {
  constructor(scene, material, shadowMaterial) {
    this.scene = scene;

    // ── Head & Hood Assembly ─────────────────────────
    this.headGeometry = buildCobraHeadGeometry();
    this.headMesh = new THREE.Mesh(this.headGeometry, material);

    this.tongueGeometry = buildTongueGeometry();
    this.tongueMesh = new THREE.Mesh(this.tongueGeometry, material);

    this.headGroup = new THREE.Group();
    this.headGroup.add(this.headMesh);
    this.headGroup.add(this.tongueMesh);
    scene.add(this.headGroup);

    this.headShadowGeometry = new THREE.BoxGeometry(1.2, 0.04, 1.2);
    this.headShadow = new THREE.Mesh(this.headShadowGeometry, shadowMaterial);
    this.headShadow.renderOrder = 1;
    scene.add(this.headShadow);

    // ── Trailing Body & Tail Segments ─────────────────
    this.numSegments = 8;
    this.segmentGeometries = [];
    this.segments = [];
    this.segmentShadowGeometries = [];
    this.segmentShadows = [];

    for (let i = 0; i < this.numSegments; i++) {
      const geo = buildCobraSegmentGeometry(i, this.numSegments);
      this.segmentGeometries.push(geo);
      const mesh = new THREE.Mesh(geo, material);
      this.segments.push(mesh);
      scene.add(mesh);

      const sGeo = new THREE.BoxGeometry(0.5, 0.04, 0.5);
      this.segmentShadowGeometries.push(sGeo);
      const sMesh = new THREE.Mesh(sGeo, shadowMaterial);
      sMesh.renderOrder = 1;
      this.segmentShadows.push(sMesh);
      scene.add(sMesh);
    }

    this.medianRows = [];
    this.state = CobraState.HIDDEN;
    this.stateTimer = 0;
    this.activeTimer = 0;

    this.x = 0;
    this.row = 0;
    this.z = 0;

    this.isSlithering = false;
    this.slitherDir = 1;

    this.hide();
    this.resetTimer();
  }

  setLevelRows(rows) {
    this.medianRows = rows.filter((r) => r.type === 'grass' && r.kind === 'median');
    this.reset();
  }

  reset() {
    this.state = CobraState.HIDDEN;
    this.stateTimer = 0;
    this.activeTimer = 0;
    this.isSlithering = false;
    this.resetTimer();
    this.hide();
  }

  resetTimer() {
    const min = COBRA.INTERVAL_MIN || 3.5;
    const max = COBRA.INTERVAL_MAX || 7.0;
    this.spawnDelay = min + Math.random() * (max - min);
  }

  hide() {
    this.headGroup.visible = false;
    this.headShadow.visible = false;
    for (let i = 0; i < this.segments.length; i++) {
      this.segments[i].visible = false;
      this.segmentShadows[i].visible = false;
    }
  }

  show() {
    this.headGroup.visible = true;
    this.headShadow.visible = true;
    for (let i = 0; i < this.segments.length; i++) {
      this.segments[i].visible = true;
      this.segmentShadows[i].visible = true;
    }
  }

  emerge() {
    if (this.medianRows.length === 0) {
      this.resetTimer();
      return;
    }

    const targetRowObj = this.medianRows[Math.floor(Math.random() * this.medianRows.length)];
    this.row = targetRowObj.row;
    this.z = -this.row * ROW_DEPTH;

    const slitherChance = COBRA.SLITHER_CHANCE ?? 0.75;
    this.isSlithering = Math.random() < slitherChance;

    if (this.isSlithering) {
      this.slitherDir = Math.random() < 0.5 ? 1 : -1;
      const spawnAtEdge = Math.random() < 0.5;

      if (spawnAtEdge) {
        // Emerge from off-screen / edge and slither across entire grass row
        this.x = this.slitherDir > 0 ? -X_LIMIT - 1.5 : X_LIMIT + 1.5;
      } else {
        // Emerge near player / median center and slither towards edge
        const p = gameState.player;
        if (Math.random() < 0.7 && p) {
          const offset = (Math.random() - 0.5) * 8;
          this.x = Math.max(-X_LIMIT, Math.min(X_LIMIT, p.x + offset));
        } else {
          this.x = (Math.random() - 0.5) * X_LIMIT * 1.6;
        }
      }
    } else {
      // Stationary pop-up
      this.slitherDir = 0;
      const p = gameState.player;
      if (Math.random() < 0.7 && p) {
        const offset = (Math.random() - 0.5) * 10;
        this.x = Math.max(-X_LIMIT, Math.min(X_LIMIT, p.x + offset));
      } else {
        this.x = (Math.random() - 0.5) * X_LIMIT * 1.8;
      }
    }

    this.state = CobraState.EMERGE;
    this.stateTimer = COBRA.WARN_DURATION || 0.6;
    this.activeTimer = 0;
    this.show();
    this.updateSegments(0, -1.2);

    eventBus.emit('cobra:hiss', { x: this.x, z: this.z, row: this.row });
  }

  update(dt, elapsed) {
    if (!gameState.game.isPlaying && !gameState.game.started) {
      this.hide();
      return;
    }

    this.activeTimer += dt;

    switch (this.state) {
      case CobraState.HIDDEN: {
        this.hide();
        this.spawnDelay -= dt;
        if (this.spawnDelay <= 0) {
          this.emerge();
        }
        break;
      }

      case CobraState.EMERGE: {
        this.stateTimer -= dt;
        const total = COBRA.WARN_DURATION || 0.6;
        const progress = Math.min(1, Math.max(0, 1 - this.stateTimer / total));
        const yHead = -1.2 + progress * 1.2;

        if (this.isSlithering) {
          const speed = COBRA.SLITHER_SPEED || 6.0;
          this.x += this.slitherDir * speed * 0.4 * dt;
        }

        this.updateSegments(progress, yHead);

        if (this.stateTimer <= 0) {
          this.state = CobraState.ACTIVE;
          this.stateTimer = COBRA.ACTIVE_DURATION || 4.0;
        }
        break;
      }

      case CobraState.ACTIVE: {
        this.stateTimer -= dt;

        if (this.isSlithering) {
          const speed = COBRA.SLITHER_SPEED || 6.0;
          this.x += this.slitherDir * speed * dt;

          this.updateSegments(1.0, 0);

          const offscreenMargin = 5.5; // wider margin so whole tail exits cleanly
          const exited = (this.slitherDir > 0 && this.x > X_LIMIT + offscreenMargin) ||
                         (this.slitherDir < 0 && this.x < -X_LIMIT - offscreenMargin);

          if (exited) {
            this.state = CobraState.HIDDEN;
            this.resetTimer();
            this.hide();
          } else if (this.stateTimer <= 0) {
            this.state = CobraState.RETREAT;
            this.stateTimer = COBRA.RETREAT_DURATION || 0.5;
          }
        } else {
          this.updateSegments(1.0, 0);

          if (this.stateTimer <= 0) {
            this.state = CobraState.RETREAT;
            this.stateTimer = COBRA.RETREAT_DURATION || 0.5;
          }
        }
        break;
      }

      case CobraState.RETREAT: {
        this.stateTimer -= dt;
        const total = COBRA.RETREAT_DURATION || 0.5;
        const progress = Math.min(1, Math.max(0, this.stateTimer / total));
        const yHead = -1.2 + progress * 1.2;

        this.updateSegments(progress, yHead);

        if (this.stateTimer <= 0) {
          this.state = CobraState.HIDDEN;
          this.resetTimer();
          this.hide();
        }
        break;
      }
    }
  }

  updateSegments(progress, yOffset) {
    const wiggleFreq = 10.0;
    const flick = Math.sin(this.activeTimer * 18) > 0.1 ? 1.4 : 0.4;
    this.tongueMesh.scale.set(1, 1, flick);

    if (this.isSlithering) {
      // ── SLITHERING MOTION ─────────────────────────────
      const baseAngle = this.slitherDir > 0 ? -Math.PI / 2 : Math.PI / 2;
      const headWiggle = Math.sin(this.activeTimer * wiggleFreq) * 0.35;
      const headZOffset = Math.sin(this.activeTimer * wiggleFreq) * 0.35;

      const headX = this.x;
      const headZ = this.z + headZOffset;

      this.headGroup.position.set(headX, yOffset, headZ);
      this.headGroup.rotation.y = baseAngle + headWiggle;

      this.headShadow.position.set(headX, 0.02, headZ);
      const hScale = Math.max(0.2, progress);
      this.headShadow.scale.set(hScale, 1, hScale);

      // Trailing body & tail segments
      const segSpacing = 0.42;
      for (let i = 0; i < this.segments.length; i++) {
        const segMesh = this.segments[i];
        const segShadow = this.segmentShadows[i];

        const phaseLag = (i + 1) * 0.45;
        const segX = this.x - this.slitherDir * (i + 1) * segSpacing;
        const wave = Math.sin(this.activeTimer * wiggleFreq - phaseLag);
        const waveCos = Math.cos(this.activeTimer * wiggleFreq - phaseLag);

        const segZ = this.z + wave * 0.45;
        const segAngle = baseAngle - waveCos * 0.45 * this.slitherDir;

        const segProgress = Math.min(1, Math.max(0, progress * 1.3 - i * 0.08));
        const segY = -1.0 * (1 - segProgress);

        segMesh.position.set(segX, segY, segZ);
        segMesh.rotation.y = segAngle;

        segShadow.position.set(segX, 0.02, segZ);
        const sScale = (1.0 - i * 0.08) * Math.max(0.1, segProgress);
        segShadow.scale.set(sScale, 1, sScale);
      }
    } else {
      // ── STATIONARY COILED POP-UP ─────────────────────
      const sway = Math.sin(this.activeTimer * 4.5) * 0.22;
      this.headGroup.position.set(this.x, yOffset, this.z);
      this.headGroup.rotation.y = Math.PI + sway;
      this.headGroup.rotation.z = Math.sin(this.activeTimer * 3.0) * 0.06;

      this.headShadow.position.set(this.x, 0.02, this.z);
      const hScale = Math.max(0.2, progress);
      this.headShadow.scale.set(hScale, 1, hScale);

      // Body segments coil around the base
      for (let i = 0; i < this.segments.length; i++) {
        const segMesh = this.segments[i];
        const segShadow = this.segmentShadows[i];

        const coilRadius = 0.35 + i * 0.06;
        const coilAngle = Math.PI * 0.3 + i * 0.65 + sway * 0.3;

        const segX = this.x + Math.cos(coilAngle) * coilRadius;
        const segZ = this.z + Math.sin(coilAngle) * coilRadius;

        const segY = yOffset + Math.max(0, (progress - 1.0));
        const segProgress = Math.min(1, Math.max(0, progress));

        segMesh.position.set(segX, segY, segZ);
        segMesh.rotation.y = coilAngle + Math.PI / 2;

        segShadow.position.set(segX, 0.02, segZ);
        const sScale = (1.0 - i * 0.08) * Math.max(0.1, segProgress);
        segShadow.scale.set(sScale, 1, sScale);
      }
    }
  }

  hitsPlayer(px, pRow, playerHalfWidth) {
    if (this.state !== CobraState.ACTIVE && this.state !== CobraState.EMERGE) return false;
    if (this.state === CobraState.EMERGE && this.stateTimer > 0.2) return false;

    if (pRow !== this.row) return false;

    const hitRadius = (COBRA.HIT_RADIUS || 0.95) + playerHalfWidth;

    if (this.isSlithering) {
      const tailX = this.x - this.slitherDir * (this.numSegments * 0.42);
      const minX = Math.min(this.x, tailX);
      const maxX = Math.max(this.x, tailX);
      return px >= minX - hitRadius && px <= maxX + hitRadius;
    } else {
      return Math.abs(px - this.x) <= hitRadius + 0.3;
    }
  }

  dispose() {
    this.hide();
    this.headGroup.removeFromParent();
    this.headShadow.removeFromParent();
    this.headGeometry.dispose();
    this.tongueGeometry.dispose();
    this.headShadowGeometry.dispose();

    for (let i = 0; i < this.segments.length; i++) {
      this.segments[i].removeFromParent();
      this.segmentShadows[i].removeFromParent();
      this.segmentGeometries[i].dispose();
      this.segmentShadowGeometries[i].dispose();
    }
  }
}
